import { useState } from "react";
import { CheckCircle2, MessageCircle } from "lucide-react";
import { api, errMsg, waLink } from "@/lib/api";
import { useSite } from "@/lib/site";
import { ZONE_NAMES, TYPES } from "@/lib/constants";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";

const empty = { name: "", phone: "", zone: "", property_type: "", bedrooms: "", budget: "", comments: "", website: "" };

function Field({ label, children }) {
  return <label className="block space-y-1.5"><span className="text-sm font-medium text-slate-700">{label}</span>{children}</label>;
}

const sel = "w-full h-10 rounded-md border border-input bg-white px-3 text-sm";

export default function OwnerLeadForm() {
  const { settings } = useSite();
  const [f, setF] = useState(empty);
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(null);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const msg = (d) => [
    "*Nuevo propietario desde la web SoloRentaPC*",
    "(Formulario \"Soy Propietario\")",
    "",
    `Nombre: ${d.name}`,
    `WhatsApp: ${d.phone}`,
    `Zona: ${d.zone || "No indicada"}`,
    `Tipo de propiedad: ${d.property_type || "No indicado"}`,
    `Habitaciones: ${d.bedrooms || "No indicado"}`,
    `Precio esperado: ${d.budget || "No indicado"}`,
    `Comentarios: ${d.comments || "Ninguno"}`,
  ].join("\n");

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/public/leads", { ...f, consent: true, lead_type: "propietario", source: "formulario_propietario" });
      const link = waLink(settings.whatsapp, msg(f));
      setDone({ link });
      window.open(link, "_blank", "noopener");
      setF(empty);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setSending(false);
    }
  };

  if (done) {
    return (
      <div className="text-center space-y-5 py-8" data-testid="owner-form-success">
        <CheckCircle2 className="mx-auto text-emerald-500" size={52} />
        <h3 className="text-2xl font-bold">Recibimos tus datos</h3>
        <p className="text-slate-600 max-w-md mx-auto">Guardamos tu solicitud. Preparamos un mensaje de WhatsApp con tus datos: <strong>revisa y pulsa enviar en WhatsApp</strong> para confirmarlo.</p>
        <a href={done.link} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 rounded-full bg-[#25D366] text-white font-semibold px-6 py-3" data-testid="owner-form-open-whatsapp"><MessageCircle size={18} />Abrir WhatsApp</a>
        <div><button onClick={() => setDone(null)} className="text-sm text-slate-500 underline" data-testid="owner-form-new">Enviar otra propiedad</button></div>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="space-y-4" data-testid="owner-lead-form">
      <input type="text" name="website" value={f.website} onChange={set("website")} className="hidden" tabIndex={-1} autoComplete="off" />
      <div className="grid sm:grid-cols-2 gap-4">
        <Field label="Nombre *"><Input required minLength={2} value={f.name} onChange={set("name")} data-testid="owner-form-name" /></Field>
        <Field label="WhatsApp *"><Input required minLength={7} value={f.phone} onChange={set("phone")} placeholder="+1 809 000 0000" data-testid="owner-form-phone" /></Field>
        <Field label="Zona">
          <select className={sel} value={f.zone} onChange={set("zone")} data-testid="owner-form-zone"><option value="">Selecciona</option>{ZONE_NAMES.map((z) => <option key={z}>{z}</option>)}</select>
        </Field>
        <Field label="Tipo de propiedad">
          <select className={sel} value={f.property_type} onChange={set("property_type")} data-testid="owner-form-type"><option value="">Selecciona</option>{TYPES.map((z) => <option key={z}>{z}</option>)}</select>
        </Field>
        <Field label="Habitaciones"><Input type="number" min="0" value={f.bedrooms} onChange={set("bedrooms")} data-testid="owner-form-bedrooms" /></Field>
        <Field label="Precio esperado (mensual)"><Input value={f.budget} onChange={set("budget")} placeholder="US$ 1,500" data-testid="owner-form-price" /></Field>
      </div>
      <Field label="Comentarios (opcional)"><Textarea rows={3} value={f.comments} onChange={set("comments")} data-testid="owner-form-comments" /></Field>
      <button disabled={sending} className="w-full rounded-full bg-[#FF3333] hover:bg-[#E62E2E] disabled:opacity-60 text-white font-semibold py-3.5 transition-colors" data-testid="owner-form-submit">
        {sending ? "Enviando..." : "Enviar"}
      </button>
    </form>
  );
}
